<template>
  <v-card
    outlined
    v-on:keydown.stop
  >
    <v-card-text>
      <div id="unratable-reason-section">
        <v-card-subtitle>Ratability</v-card-subtitle>
        <v-container id="unratable-reason-container">
          <v-checkbox
            label="Unratable"
            v-if="!$_isUnratable"
            v-model="$_isUnratable"
          >
          </v-checkbox>
          <v-radio-group
            v-else
            v-bind:value="cefrScoreSegment.data.unratableReasonId"
            v-on:change="$_updateCefrScoreUnratableReasonId"
          >
            <v-radio
              v-for="(unratableReason, unratableReasonId) in $_unratableReasons"
              v-bind:key="unratableReasonId"
              v-bind:label="unratableReason"
              v-bind:value="unratableReasonId"
            />
          </v-radio-group>
          <general-textarea
            dense
            v-if="$_isUnratableReasonOther"
            v-model="$data.$_unratableReason"
            v-bind:rows="$_numRowsOfUnratableReason"
            v-on:blur="$_saveUnratableReason"
          >
          </general-textarea>
        </v-container>
      </div>

      <div
        id="segment-info-section"
        v-if="!cefrScoreSegment.hasNoRange"
      >
        <v-card-subtitle>Segment</v-card-subtitle>
        <v-container id="segment-info-container">
          <CefrScoreEditorSeekToSegmentButton
            v-bind:cefr-score-timeline-segment-id="cefrScoreTimelineSegmentId"
          />
          <dl id="segment-time-container">
            <dt>begin</dt>
            <dd>{{ $_beginTimeFormatString }}</dd>
            <dt>end</dt>
            <dd>{{ $_endTimeFormatString }}</dd>
          </dl>
        </v-container>
      </div>

      <div id="cefr-score-section">
        <v-card-subtitle>Score</v-card-subtitle>
        <v-container class="d-flex flex-row">
          <div class="flex-shrink-0 mr-10 py-5">
            <CefrScoreEditorSlider
              is-vertical
              v-bind:is-unratable="$_isUnratable"
              v-bind:external-cefr-score="$_externalCefrScore"
              v-bind:cefr-score-timeline-segment-id="cefrScoreTimelineSegmentId"
              v-on:update="$_updateCefrScore"
            />
          </div>

          <div class="flex-shrink-1" v-if="$_cefrScoreDefinitions">
            <v-simple-table>
              <tbody>
                <tr><td>C2</td><td>{{ $_cefrScoreDefinitions.C2 }}</td></tr>
                <tr><td>C1</td><td>{{ $_cefrScoreDefinitions.C1 }}</td></tr>
                <tr><td>B2</td><td>{{ $_cefrScoreDefinitions.B2 }}</td></tr>
                <tr><td>B1</td><td>{{ $_cefrScoreDefinitions.B1 }}</td></tr>
                <tr><td>A2</td><td>{{ $_cefrScoreDefinitions.A2 }}</td></tr>
                <tr><td>A1</td><td>{{ $_cefrScoreDefinitions.A1 }}</td></tr>
              </tbody>
            </v-simple-table>
          </div>
        </v-container>
      </div>

      <v-card-subtitle>Score Reason</v-card-subtitle>
      <v-container>
        <general-textarea
          dense
          v-model="$data.$_scoreReason"
          v-bind:disabled="$_isUnratable"
          v-bind:rows="$_numRowsOfDescription"
          v-on:blur="$_saveScoreReason"
        >
        </general-textarea>
      </v-container>

      <template v-if="$_hasReferences">
        <v-card-subtitle>References</v-card-subtitle>
        <v-container fluid pt-0>
          <v-row no-gutters>
            <v-col
              col="4" md="6"
              v-for="(refClass, refClassIdx) of $_refClasses"
              v-bind:key="refClassIdx"
            >
              <v-card flat>
                <v-subheader>
                  <span>Aspect: {{ refClass }}</span>
                  <v-spacer />
                  <v-btn
                    outlined text x-small fab
                    v-bind:disabled="!$_isReferenceRegistered[refClass]"
                    v-on:click="$_emitUnregisterAllReferences(refClass)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                  <v-btn
                    outlined text x-small fab
                    v-on:click="$_toggleSelectSegmentsCallbackRegistoration(refClass, $event)"
                  >
                    <v-icon>mdi-playlist-plus</v-icon>
                  </v-btn>
                </v-subheader>
                <v-btn
                  text small tile
                  v-for="(timelineSegmentIdReferringTo, referenceId) in $data.$_timelineSegmentIdsReferringTo[refClass]"
                  v-bind:key="referenceId"
                  v-on:click="selectTimelineSegmentAndSeek(timelineSegmentIdReferringTo)"
                >
                  {{ timelineSegmentIdReferringTo.mergedId }}
                </v-btn>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
        small text
        v-on:click="$_foldPanel"
      >
        Fold Panel
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
div#unratable-reason-section,
div#segment-info-section,
div#cefr-score-section {
  display: flex;
  flex-direction: row;
  align-items: center;
}

div#segment-info-container {
  display: flex;
  flex-direction: row;
  align-items: center;
}

dl#segment-time-container {
  display: flex;
  flex-direction: row;
}

dl#segment-time-container dt {
  margin-right: 5px;
}

dl#segment-time-container dd:not(:last-child) {
  margin-right: 10px;
}

dl#segment-time-container dt:after {
  content: ':';
}

.attached-ref-class {
  background-color: #f03e1e !important;
}
</style>

<script>
import {
  TimelineSegmentId,
  GeneralTextarea,
  Reference,
  referencePool,
  providedFunctions,
  utils,
} from '@Scatt'
import CefrScoreEditorSlider from './CefrScoreEditorSlider.vue'
import CefrScoreEditorSeekToSegmentButton from './CefrScoreEditorSeekToSegmentButton.vue'
import CefrScoreSegmentData from '../../../modules/segmentDataTypes/CefrScoreSegmentData.js'

export default {
  components: {
    CefrScoreEditorSlider,
    CefrScoreEditorSeekToSegmentButton,
    GeneralTextarea,
  },

  watch: {
    'cefrScoreSegment.data.scoreReason': {
      handler(scoreReason) {
        this.$data.$_scoreReason = String(scoreReason);
      },
      immediate: true,
    },

    'cefrScoreSegment.data.unratableReason': {
      handler(unratableReason) {
        this.$data.$_unratableReason = String(unratableReason);
      },
      immediate: true,
    },

    $_isUnratableReasonOther: {
      handler(isUnratableReasonOther) {
        if (!isUnratableReasonOther) {
          this.$data.$_unratableReason = '';
        }
      },
      immediate: true,
    }
  },

  props: {
    name: { type: String },
    cefrScoreSegment: { type: CefrScoreSegmentData },
    cefrScoreTimelineSegmentId: { type: TimelineSegmentId },
  },

  data() {
    return {
      $_registerSelectSegmentsCallbackButtonElement: null,
      $_timelineSegmentIdsReferringTo: {},
      $_onReferencePoolRegisterCallbackId: null,
      $_onReferencePoolUnregisterCallbackId: null,
      $_scoreReason: '',
      $_unratableReason: '',
    };
  },

  created() {
    for (let refClass of this.$_refClasses) {
      this.$set(this.$data.$_timelineSegmentIdsReferringTo, refClass, {});
    }
    let allReferences = referencePool.getAllReferences();
    this.$_createTimelineSegmentIdCache(Object.keys(allReferences), Object.values(allReferences));
    this.$data.$_onReferencePoolRegisterCallbackId = referencePool.registerOnRegisterCallback(this.$_createTimelineSegmentIdCache);
    this.$data.$_onReferencePoolUnregisterCallbackId = referencePool.registerOnUnregisterCallback(this.$_deleteTimelineSegmentIdCache);
  },

  destroyed() {
    if (this.$_isSelectSegmentsCallbackRegistered) {
      this.$_unregisterSelectSegmentsCallback();
    }
    referencePool.unregisterOnRegisterCallback(this.$data.$_onReferencePoolRegisterCallbackId);
    referencePool.unregisterOnUnregisterCallback(this.$data.$_onReferencePoolUnregisterCallbackId);
  },

  computed: {
    $_externalCefrScore() { return this.cefrScoreSegment.data.score; },
    $_beginTimeFormatString() { return (this.cefrScoreSegment.begin === null)? '-' : this.cefrScoreSegment.begin.toFormatString(); },
    $_endTimeFormatString() { return (this.cefrScoreSegment.end === null)? '-' : this.cefrScoreSegment.end.toFormatString(); },
    $_refClasses() { return this.cefrScoreSegment.data.aspects; },
    $_isSelectSegmentsCallbackRegistered() { return (this.$data.$_registerSelectSegmentsCallbackButtonElement !== null); },
    $_numRowsOfDescription() { return utils.countNumLines(this.$data.$_scoreReason); },
    $_numRowsOfUnratableReason() { return utils.countNumLines(this.$data.$_unratableReason); },
    $_hasReferences() { return (this.$_refClasses.length > 0); },
    $_isReferenceRegistered() {
      let isReferenceRegistered = {};
      for (let currentRefClass of this.$_refClasses) {
        let timelineSegmentIdsRefferingToCurrentRefClass = this.$data.$_timelineSegmentIdsReferringTo[currentRefClass];
        if (timelineSegmentIdsRefferingToCurrentRefClass) {
          isReferenceRegistered[currentRefClass] = (Object.keys(timelineSegmentIdsRefferingToCurrentRefClass).length > 0);
        } else {
          isReferenceRegistered[currentRefClass] = false;
        }
      }
      return isReferenceRegistered;
    },
    $_isScoreReasonSameAsLastSaved() {
      if (!this.$data.$_scoreReason) return true;
      return (this.$data.$_scoreReason === this.cefrScoreSegment.data.scoreReason);
    },
    $_isUnratableReasonSameAsLastSaved() {
      if (!this.$data.$_unratableReason) return true;
      return (this.$data.$_unratableReason === this.cefrScoreSegment.data.unratableReason);
    },
    $_isUnratable: {
      get() {
        return CefrScoreSegmentData.contentData.unratableReasons[this.cefrScoreSegment.data.unratableReasonId] !== 'Ratable';
      },
      set(isUnratable) {
        if (isUnratable) {
          this.$_updateCefrScoreUnratableReasonId('1');
        } else {
          this.$_updateCefrScoreUnratableReasonId('0');
        }
      },
    },
    $_unratableReasons() {
      return CefrScoreSegmentData.contentData.unratableReasons;
    },
    $_isUnratableReasonOther() {
      return CefrScoreSegmentData.contentData.unratableReasons[this.cefrScoreSegment.data.unratableReasonId] === 'Unratable: Other';
    },
    $_cefrScoreDefinitions() {
      switch(this.name) {
        case 'Range':
          return {
            C2: 'Shows great flexibility reformulating ideas in differing linguistic forms to convey finer shades of meaning precisely, to give emphasis, to differentiate and to eliminate ambiguity. Also has a good command of idiomatic expressions and colloquialisms.',
            C1: 'Has a good command of a broad range of language allowing him/her to select a formulation to express him/ herself clearly in an appropriate style on a wide range of general, academic, professional or leisure topics without having to restrict what he/she wants to say.',
            B2: 'Has a sufficient range of language to be able to give clear descriptions, express viewpoints on most general topics, without much conspicuous searching for words, using some complex sentence forms to do so.',
            B1: 'Has enough language to get by, with sufficient vocabulary to express him/herself with some hesitation and circumlocutions on topics such as family, hobbies and interests, work, travel, and current events.',
            A2: 'Uses basic sentence patterns with memorised phrases, groups of a few words and formulae in order to communicate limited information in simple everyday situations.',
            A1: 'Has a very basic repertoire of words and simple phrases related to personal details and particular concrete situations.',
          };
        case 'Accuracy':
          return {
            C2: 'Maintains consistent grammatical control of complex language, even while attention is otherwise engaged (e.g. in forward planning, in monitoring others\' reactions).',
            C1: 'Consistently maintains a high degree of grammatical accuracy; errors are rare, difficult to spot and generally corrected when they do occur.',
            B2: 'Shows a relatively high degree of grammatical control. Does not make errors which cause misunderstanding, and can correct most of his/her mistakes.',
            B1: 'Uses reasonably accurately a repertoire of frequently used "routines" and patterns associated with more predictable situations.',
            A2: 'Uses some simple structures correctly, but still systematically makes basic mistakes.',
            A1: 'Shows only limited control of a few simple grammatical structures and sentence patterns in a memorised repertoire.',
          };
        case 'Fluency':
          return {
            C2: 'Can express him/herself spontaneously at length with a natural colloquial flow, avoiding or backtracking around any difficulty so smoothly that the interlocutor is hardly aware of it.',
            C1: 'Can express him/herself fluently and spontaneously, almost effortlessly. Only a conceptually difficult subject can hinder a natural, smooth flow of language.',
            B2: 'Can produce stretches of language with a fairly even tempo; although he/she can be hesitant as he or she searches for patterns and expressions, there are few noticeably long pauses.',
            B1: 'Can keep going comprehensively, even though pausing for grammatical and lexical planning and repair is very evident, especially in longer stretches of free production.',
            A2: 'Can make him/herself understood in very short utterances, even though pauses, false starts and reformulation are very evident.',
            A1: 'Can manage very short, isolated, mainly pre-packaged utterances, with much pausing to search for expressions, to articulate less familiar words, and to repair communication.',
          };
        case 'Phonology':
          return {
            C2: 'Can employ the full range of phonological features in the target language with a high level of control – including prosodic features such as word and sentence stress, rhythm and intonation – so that the finer points of his/her message are clear and precise.  Intelligibility is not affected in any way by features of accent that may be retained from other language(s).',
            C1: 'Can employ the full range of phonological features in the target language with sufficient control to ensure intelligibility throughout. Can articulate virtually all the sounds of the target language; some features of accent retained from other language(s) may be noticeable, but they do not affect intelligibility at all.',
            B2: 'Can generally use appropriate intonation, place stress correctly and articulate individual sounds clearly; accent tends to be influenced by other language(s) he/she speaks, but has little or no effect on intelligibility.',
            B1: 'Pronunciation is generally intelligible; can approximate intonation and stress at both utterance and word levels. However, accent is usually influenced by other language(s) he/she speaks.',
            A2: 'Pronunciation is generally clear enough to be understood, but conversational partners will need to ask for repetition from time to time. A strong influence from other language(s) he/she speaks on stress, rhythm and intonation may affect intelligibility, requiring collaboration from interlocutors. Nevertheless, pronunciation of familiar words is clear.',
            A1: 'Pronunciation of a very limited repertoire of learnt words and phrases can be understood with some effort by interlocutors used to dealing with speakers of the language group concerned. Can reproduce correctly a limited range of sounds as well as the stress on simple, familiar words and phrases.',
          };
        case 'Interaction':
          return {
            C2: 'Can interact with ease and skill, picking up and using non-verbal and intonational cues apparently effortlessly. Can interweave his/her contribution into the joint discourse with fully natural turntaking, referencing, allusion making etc.',
            C1: 'Can select a suitable phrase from a readily available range of discourse functions to preface his remarks in order to get or to keep the floor and to relate his/her own contributions skilfully to those of other speakers.',
            B2: 'Can initiate discourse, take his/her turn when appropriate and end conversation when he/she needs to, though he/she may not always do this elegantly. Can help the discussion along on familiar ground confirming comprehension, inviting others in, etc.',
            B1: 'Can initiate, maintain and close simple face-to-face conversation on topics that are familiar or of personal interest. Can repeat back part of what someone has said to confirm mutual understanding.',
            A2: 'Can ask and answer questions and respond to simple statements. an indicate when he/she is following but is rarely able to understand enough to keep conversation going of his/her own accord.',
            A1: 'Can ask and answer questions about personal details. Can interact in a simple way but communication is totally dependent on repetition, rephrasing and repair.',
          };
        case 'Coherence':
          return {
            C2: 'Can create coherent and cohesive discourse making full and appropriate use of a variety of organisational patterns and a wide range of connectors and other cohesive devices.',
            C1: 'Can produce clear, smoothly flowing, well-structured speech, showing controlled use of organisational patterns, connectors and cohesive devices.',
            B2: 'Can use a limited number of cohesive devices to link his/her utterances into clear, coherent discourse, though there may be some "jumpiness" in a long contribution.',
            B1: 'Can link a series of shorter, discrete simple elements into a connected, linear sequence of points.',
            A2: 'Can link groups of words with simple connectors like "and, "but" and "because".',
            A1: 'Can link words or groups of words with very basic linear connectors like “and” or “then”.',
          };
        case 'Overall CEFR':
          return {
            C2: 'Has a good command of idiomatic expressions and colloquialisms with awareness of connotative levels of meaning. Can convey finer shades of meaning precisely by using, with reasonable accuracy, a wide range of modification devices. Can backtrack and restructure around a difficulty so smoothly the interlocutor is hardly aware of it.',
            C1: 'Can express him/herself fluently and spontaneously, almost effortlessly. Has a good command of a broad lexical repertoire allowing gaps to be readily overcome with circumlocutions. There is little obvious searching for expressions or avoidance strategies; only a conceptually difficult subject can hinder a natural, smooth flow of language.',
            B2: 'Can use the language fluently, accurately and effectively on a wide range of general, academic, vocational or leisure topics, marking clearly the relationships between ideas. Can communicate spontaneously with good grammatical control without much sign of having to restrict what he/she wants to say, adopting a level of formality appropriate to the circumstances. Can interact with a degree of fluency and spontaneity that makes regular interaction, and sustained relationships with speakers of the target language quite possible without imposing strain on either party. Can highlight the personal significance of events and experiences, account for and sustain views clearly by providing relevant explanations and arguments.',
            B1: 'Can communicate with some confidence on familiar routine and non-routine matters related to his/her interests and professional field. Can exchange, check and confirm information, deal with less routine situations and explain why something is a problem. Can express thoughts on more abstract, cultural topics such as films, books, music etc. Can exploit a wide range of simple language to deal with most situations likely to arise whilst travelling. Can enter unprepared into conversation of familiar topics, express personal opinions and exchange information on topics that are familiar, of personal interest or pertinent to everyday life (e.g. family, hobbies, work, travel and current events).',
            A2: 'Can interact with reasonable ease in structured situations and short conversations, provided the other person helps if necessary. Can manage simple, routine exchanges without undue effort; can ask and answer questions and exchange ideas and information on familiar topics in predictable everyday situations. Can communicate in simple and routine tasks requiring a simple and direct exchange of information on familiar and routine matters to do with work and free time. Can handle very short social exchanges but is rarely able to understand enough to keep conversation going of his/her own accord.',
            A1: 'Can interact in a simple way but communication is totally dependent on repetition at a slower rate of speech, rephrasing and repair. Can ask and answer simple questions, initiate and respond to simple statements in areas of immediate need or on very familiar topics.',
          };
        default:
          return null;
      }
    },
  },

  inject: providedFunctions,

  methods: {
    $_emitUnregisterAllReferences(refClass) {
      let referenceIds = Object.keys(this.$data.$_timelineSegmentIdsReferringTo[refClass]);
      this.unregisterReferences(referenceIds);
    },

    $_toggleSelectSegmentsCallbackRegistoration(refClass, event) {
      let registerSelectSegmentsCallbackButtonElement = event.currentTarget;
      if (this.$data.$_registerSelectSegmentsCallbackButtonElement === registerSelectSegmentsCallbackButtonElement) {
        this.$_unregisterSelectSegmentsCallback();
      } else {
        if (this.$_isSelectSegmentsCallbackRegistered) {
          this.$_unregisterSelectSegmentsCallback();
        }
        this.$_registerSelectSegmentsCallback(refClass, registerSelectSegmentsCallbackButtonElement);
      }
    },

    $_unregisterSelectSegmentsCallback() {
      if (this.$data.$_registerSelectSegmentsCallbackButtonElement) {
        this.$data.$_registerSelectSegmentsCallbackButtonElement.classList.remove('attached-ref-class');
        this.$data.$_registerSelectSegmentsCallbackButtonElement = null;
      }
      this.unregisterSelectSegmentsCallback();
    },

    $_registerSelectSegmentsCallback(refClass, registerSelectSegmentsCallbackButtonElement) {
      this.$data.$_registerSelectSegmentsCallbackButtonElement = registerSelectSegmentsCallbackButtonElement;
      this.$data.$_registerSelectSegmentsCallbackButtonElement.classList.add('attached-ref-class');
      let referFrom = this.cefrScoreTimelineSegmentId;
      this.registerSelectSegmentsCallback((selectedTimelineSegmentIds) => {
        for (let selectedTimelineSegmentId of selectedTimelineSegmentIds) {
          let foundReferenceId = null;
          for (let [ referenceId, reference ] of Object.entries(referencePool.getAllReferences())) {
            if (reference.referTo.isSameAs(selectedTimelineSegmentId) && (reference.refClass === refClass)) {
              foundReferenceId = referenceId;
              break;
            }
          }
          if (foundReferenceId) {
            this.unregisterReferences(foundReferenceId);
          } else {
            let reference = new Reference(refClass, referFrom, selectedTimelineSegmentId);
            this.registerReferences(reference);
          }
        }
      });
    },

    $_createTimelineSegmentIdCache(registeredReferenceIds, registeredReferences) {
      let numRegisteredReferences = registeredReferences.length;
      for (let registeredReferenceIdx = 0; registeredReferenceIdx < numRegisteredReferences; ++registeredReferenceIdx) {
        let registeredReferenceId = registeredReferenceIds[registeredReferenceIdx];
        let registeredReference = registeredReferences[registeredReferenceIdx];
        for (let refClass of this.$_refClasses) {
          let currentTimelineSegmentId = this.cefrScoreTimelineSegmentId;
          if (registeredReference.referFrom.isSameAs(currentTimelineSegmentId) && (registeredReference.refClass === refClass)) {
            this.$set(this.$data.$_timelineSegmentIdsReferringTo[refClass], registeredReferenceId, registeredReference.referTo);
          }
        }
      }
    },

    $_deleteTimelineSegmentIdCache(unregisteredReferenceIds) {
      let numUnregisteredReferences = unregisteredReferenceIds.length;
      for (let unregisteredReferenceIdx = 0; unregisteredReferenceIdx < numUnregisteredReferences; ++unregisteredReferenceIdx) {
        let unregisteredReferenceId = unregisteredReferenceIds[unregisteredReferenceIdx];
        for (let refClass of this.$_refClasses) {
          if (Object.keys(this.$data.$_timelineSegmentIdsReferringTo[refClass]).includes(unregisteredReferenceId)) {
            this.$delete(this.$data.$_timelineSegmentIdsReferringTo[refClass], unregisteredReferenceId);
          }
        }
      }
    },

    $_saveScoreReason() {
      if (!this.$_isScoreReasonSameAsLastSaved) {
        this.$emit('update-cefr-score-reason', this.$data.$_scoreReason);
      }
    },

    $_saveUnratableReason() {
      if (!this.$_isUnratableReasonSameAsLastSaved) {
        this.$emit('update-cefr-score-unratable-reason', this.$data.$_unratableReason);
      }
    },

    $_updateCefrScore(cefrScore) {
      this.$emit('update-cefr-score', cefrScore);
    },

    $_updateCefrScoreUnratableReasonId(unratableReasonId) {
      this.$emit('update-cefr-score-unratable-reason-id', unratableReasonId);
    },

    $_foldPanel() {
      this.$emit('fold-panel');
    },
  },
};
</script>
